import { QueryClient } from '@tanstack/react-query';

// Cache configuration
const CACHE_CONFIG = {
  // Static data that rarely changes
  venues: { staleTime: 30 * 60 * 1000, cacheTime: 60 * 60 * 1000 }, // 30min stale, 1hr cache
  events: { staleTime: 5 * 60 * 1000, cacheTime: 30 * 60 * 1000 }, // 5min stale, 30min cache
  userProfile: { staleTime: 10 * 60 * 1000, cacheTime: 60 * 60 * 1000 }, // 10min stale, 1hr cache
  
  // Dynamic data
  guestLists: { staleTime: 1 * 60 * 1000, cacheTime: 5 * 60 * 1000 }, // 1min stale, 5min cache
  analytics: { staleTime: 30 * 1000, cacheTime: 2 * 60 * 1000 }, // 30sec stale, 2min cache
  notifications: { staleTime: 10 * 1000, cacheTime: 1 * 60 * 1000 }, // 10sec stale, 1min cache
  
  // Real-time data
  checkIns: { staleTime: 0, cacheTime: 30 * 1000 }, // No stale time, 30sec cache
  security: { staleTime: 0, cacheTime: 10 * 1000 }, // No stale time, 10sec cache
};

// Create optimized query client
export const createOptimizedQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes default
        gcTime: 30 * 60 * 1000, // 30 minutes default (renamed from cacheTime)
        retry: (failureCount, error: any) => {
          // Don't retry on 4xx errors
          if (error?.status >= 400 && error?.status < 500) {
            return false;
          }
          return failureCount < 3;
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: 1,
      },
    },
  });
};

// Cache key factories
export const cacheKeys = {
  // Venues
  venues: () => ['venues'] as const,
  venue: (id: string) => ['venues', id] as const,
  venueEvents: (venueId: string) => ['venues', venueId, 'events'] as const,
  
  // Events
  events: (filters?: Record<string, any>) => ['events', filters] as const,
  event: (id: string) => ['events', id] as const,
  eventGuestList: (eventId: string) => ['events', eventId, 'guestList'] as const,
  eventAnalytics: (eventId: string) => ['events', eventId, 'analytics'] as const,
  
  // Users
  userProfile: (userId: string) => ['users', userId, 'profile'] as const,
  userEvents: (userId: string) => ['users', userId, 'events'] as const,
  userTickets: (userId: string) => ['users', userId, 'tickets'] as const,
  userNotifications: (userId: string) => ['users', userId, 'notifications'] as const,
  
  // Security
  securityReports: (venueId: string, date?: string) => 
    ['security', venueId, 'reports', date] as const,
  securityMetrics: (venueId: string) => ['security', venueId, 'metrics'] as const,
  
  // Analytics
  analytics: (type: string, venueId?: string, timeRange?: string) =>
    ['analytics', type, venueId, timeRange] as const,
};

// Local storage cache for offline support
export class LocalStorageCache {
  private prefix = 'noche_cache_';
  private version = '1.0';

  setItem<T>(key: string, value: T, expiryMinutes = 60): void {
    try {
      const item = {
        value,
        expiry: Date.now() + (expiryMinutes * 60 * 1000),
        version: this.version,
      };
      localStorage.setItem(this.prefix + key, JSON.stringify(item));
    } catch (error) {
      console.warn('Failed to cache item in localStorage:', error);
    }
  }

  getItem<T>(key: string): T | null {
    try {
      const itemStr = localStorage.getItem(this.prefix + key);
      if (!itemStr) return null;

      const item = JSON.parse(itemStr);
      
      // Check version compatibility
      if (item.version !== this.version) {
        this.removeItem(key);
        return null;
      }

      // Check expiry
      if (Date.now() > item.expiry) {
        this.removeItem(key);
        return null;
      }

      return item.value;
    } catch (error) {
      console.warn('Failed to retrieve cached item:', error);
      return null;
    }
  }

  removeItem(key: string): void {
    localStorage.removeItem(this.prefix + key);
  }

  clear(): void {
    Object.keys(localStorage)
      .filter(key => key.startsWith(this.prefix))
      .forEach(key => localStorage.removeItem(key));
  }

  // Cache venues for offline access
  cacheVenueData(venues: any[]): void {
    this.setItem('venues_all', venues, 60); // Cache for 1 hour
    venues.forEach(venue => {
      this.setItem(`venue_${venue.id}`, venue, 60);
    });
  }

  // Cache user's tickets for offline access
  cacheUserTickets(userId: string, tickets: any[]): void {
    this.setItem(`user_tickets_${userId}`, tickets, 30); // Cache for 30 minutes
  }

  // Cache critical event data
  cacheEventData(eventId: string, eventData: any): void {
    this.setItem(`event_${eventId}`, eventData, 15); // Cache for 15 minutes
  }
}

export const localCache = new LocalStorageCache();

// Preload critical data
export const preloadCriticalData = async (queryClient: QueryClient, userId?: string) => {
  const promises: Promise<any>[] = [];

  // Preload venues (static data)
  promises.push(
    queryClient.prefetchQuery({
      queryKey: cacheKeys.venues(),
      queryFn: () => fetch('/api/venues').then(res => res.json()),
      staleTime: CACHE_CONFIG.venues.staleTime,
    })
  );

  // Preload current user's data if authenticated
  if (userId) {
    promises.push(
      queryClient.prefetchQuery({
        queryKey: cacheKeys.userProfile(userId),
        queryFn: () => fetch(`/api/users/${userId}/profile`).then(res => res.json()),
        staleTime: CACHE_CONFIG.userProfile.staleTime,
      })
    );

    promises.push(
      queryClient.prefetchQuery({
        queryKey: cacheKeys.userTickets(userId),
        queryFn: () => fetch(`/api/users/${userId}/tickets`).then(res => res.json()),
        staleTime: CACHE_CONFIG.events.staleTime,
      })
    );
  }

  await Promise.allSettled(promises);
};

// Smart cache invalidation
export const invalidateRelatedCache = (queryClient: QueryClient, action: string, entityId?: string) => {
  switch (action) {
    case 'venue_updated':
      if (entityId) {
        queryClient.invalidateQueries({ queryKey: cacheKeys.venue(entityId) });
        queryClient.invalidateQueries({ queryKey: cacheKeys.venueEvents(entityId) });
      }
      queryClient.invalidateQueries({ queryKey: cacheKeys.venues() });
      break;

    case 'event_updated':
      if (entityId) {
        queryClient.invalidateQueries({ queryKey: cacheKeys.event(entityId) });
        queryClient.invalidateQueries({ queryKey: cacheKeys.eventGuestList(entityId) });
        queryClient.invalidateQueries({ queryKey: cacheKeys.eventAnalytics(entityId) });
      }
      queryClient.invalidateQueries({ queryKey: ['events'] });
      break;

    case 'user_profile_updated':
      if (entityId) {
        queryClient.invalidateQueries({ queryKey: cacheKeys.userProfile(entityId) });
      }
      break;

    case 'guest_list_updated':
      if (entityId) {
        queryClient.invalidateQueries({ queryKey: cacheKeys.eventGuestList(entityId) });
      }
      break;

    case 'security_event':
      queryClient.invalidateQueries({ queryKey: ['security'] });
      break;

    default:
      // Generic invalidation
      queryClient.invalidateQueries();
  }
};

// Network-aware caching
export const getNetworkAwareCacheConfig = () => {
  const connection = (navigator as any).connection;
  const isSlowNetwork = connection && (
    connection.effectiveType === 'slow-2g' || 
    connection.effectiveType === '2g' ||
    connection.downlink < 1.5
  );

  if (isSlowNetwork) {
    return {
      staleTime: 15 * 60 * 1000, // 15 minutes for slow networks
      cacheTime: 60 * 60 * 1000, // 1 hour
      retry: 1, // Fewer retries
    };
  }

  return {
    staleTime: 5 * 60 * 1000, // 5 minutes for fast networks
    cacheTime: 30 * 60 * 1000, // 30 minutes
    retry: 3,
  };
};